import React from 'react';

import { BrowserTitleService } from '~commonInfra/services/BrowserTitleService';
import { Location } from '~commonInfra/utils/Location';
import { Tableau } from '~commonInfra/utils/TableauLog';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { updateBreadcrumbs } from '~src/spacesPlatform/actions/UpdateBreadcrumbsAction';
import { BreadcrumbStateInfo } from '~src/spacesPlatform/components/breadcrumbs/BreadcrumbStateInfo';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { Users } from '~src/userManagement/services/resources/Users';
import { IUserInfo } from '~src/userManagement/users/models/IUserInfo';

import { GetContentRelayPlanRequest } from '../serverApi/requests/GetContentRelayPlanRequest';
import { IContentRelayPlanDetail } from '../serverApi/types/ISelectionQuery';
import { ContentRelayPlanDetailsSpace } from './ContentRelayPlanDetailsSpace';

export interface ContentRelayPlanDetailsSpaceContainerProps {
  /** LUID of the plan being shown, parsed from the route. */
  readonly planLuid: string;
  readonly urls: SpacesSiteUrls;
}

interface ContentRelayPlanDetailsSpaceContainerState {
  readonly isLoading: boolean;
  readonly hasError: boolean;
  readonly plan: IContentRelayPlanDetail | null;
  readonly owner: IUserInfo | null;
}

/** Breadcrumbs: "All Content Relays / <plan name>". */
export function setPlanBreadcrumbs(urls: SpacesSiteUrls, planName?: string): void {
  const listUrl = urls.allContentRelayPlansUrl();
  const crumbs: BreadcrumbStateInfo[] = [{ id: listUrl, text: Messages.relayPlanDetailsAllRelaysBreadcrumb(), href: listUrl }];
  if (planName) {
    crumbs.push({ text: planName });
  }
  getAppStore().dispatch(updateBreadcrumbs(crumbs));
}

/**
 * Container for the Content Relay plan detail page. Loads the plan (`getPlan`) and its owner, owns the
 * loading / error state, and re-reads the plan after edits made on the page.
 */
export class ContentRelayPlanDetailsSpaceContainer
  extends React.Component<ContentRelayPlanDetailsSpaceContainerProps, ContentRelayPlanDetailsSpaceContainerState> {
  private isMounted_ = false;
  // Bumped per loadPlan so only the latest load (e.g. after two quick edits) updates state.
  private loadGeneration_ = 0;

  public constructor(props: ContentRelayPlanDetailsSpaceContainerProps) {
    super(props);
    this.state = { isLoading: !!props.planLuid, hasError: !props.planLuid, plan: null, owner: null };
  }

  public componentDidMount() {
    this.isMounted_ = true;
    setPlanBreadcrumbs(this.props.urls);
    this.loadPlan(true);
  }

  public componentDidUpdate(prevProps: ContentRelayPlanDetailsSpaceContainerProps) {
    // The route reuses this instance when navigating from one plan straight to another, so refetch here.
    if (prevProps.planLuid !== this.props.planLuid) {
      this.loadPlan(true);
    }
  }

  public componentWillUnmount() {
    this.isMounted_ = false;
  }

  public render() {
    return (
      <ContentRelayPlanDetailsSpace
        isLoading={this.state.isLoading}
        hasError={this.state.hasError}
        plan={this.state.plan}
        owner={this.state.owner}
        urls={this.props.urls}
        onPlanChanged={this.onPlanChanged}
        onPlanDeleted={this.onReturnToList}
        onReturnToList={this.onReturnToList}
      />
    );
  }

  /** Loads the plan. `showSpinner` is false for a quiet re-read after an edit on the page. */
  private async loadPlan(showSpinner: boolean): Promise<void> {
    const planLuid = this.props.planLuid;
    const generation = ++this.loadGeneration_;
    const isCurrent = (): boolean => this.isMounted_ && this.props.planLuid === planLuid && this.loadGeneration_ === generation;
    if (!planLuid) {
      this.setState({ isLoading: false, hasError: true, plan: null, owner: null });
      return;
    }
    if (showSpinner) {
      this.setState({ isLoading: true, hasError: false, plan: null, owner: null });
    }
    try {
      const plan: any = await WebHttpClient.sendRequest(new GetContentRelayPlanRequest({ luid: planLuid }));
      // The web client can resolve HTTP 200 with an `errors` payload (e.g. plan not found).
      if (!plan || (plan.errors && plan.errors.length) || !plan.luid) {
        throw plan;
      }
      const owner = await this.loadOwner(plan.ownerId);
      if (!isCurrent()) {
        return;
      }
      this.setState({ isLoading: false, hasError: false, plan: plan, owner: owner });
      setPlanBreadcrumbs(this.props.urls, plan.name);
      BrowserTitleService.setTitleWithTranslatedSpace({ translatedSpaceName: plan.name });
    } catch (error) {
      Tableau.Logger().info('Content Relay: failed to load plan ' + planLuid + ': ' + error);
      if (isCurrent()) {
        this.setState({ isLoading: false, hasError: true, plan: null, owner: null });
      }
    }
  }

  // The owner is decoration — a failed lookup leaves it blank rather than failing the page.
  // listPlans reads ownerId 0 as "unset" (tabhf-content-mobility filters on ownerId > 0), so skip it too.
  // proto3 JSON sends int64 as a string, so "0" must be caught as well as 0.
  private loadOwner(ownerId: number | string): Promise<IUserInfo | null> {
    if (!(Number(ownerId) > 0)) {
      return Promise.resolve(null);
    }
    return Users.instance.getUsersById([String(ownerId)])
      .then(function(result) { return result && result.items && result.items.length ? result.items[0] as IUserInfo : null; })
      .catch(function() { return null; });
  }

  private onPlanChanged = (): void => {
    this.loadPlan(false);
  };

  private onReturnToList = (): void => {
    Location.instance.setUrl(this.props.urls.allContentRelayPlansUrl());
  };
}
